import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslocoRootModule } from 'app/transloco-root.module';
import { IgntrIconComponent } from 'app/shared/igntr';
import { DashboardCardComponent } from './dashboard-card.component';
import { ToolboxForListDto } from 'app/api/models/toolbox/toolboxForListDto';

interface ToolView {
  id: string;
  title: string;
  meta: string;
  isNew: boolean;
}

// Anything created within this window gets the "New" badge.
const NEW_BADGE_DAYS = 7;

@Component({
  selector: 'app-dashboard-featured-tools',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslocoRootModule,
    IgntrIconComponent,
    DashboardCardComponent,
  ],
  template: `
    <app-dashboard-card
      [title]="'dashToolsTitle' | transloco"
      [sub]="'dashToolsSub' | transloco"
    >
      <a
        card-action
        routerLink="/toolbox/areas"
        class="text-[14px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
      >
        {{ 'dashToolsSeeAll' | transloco: { count: totalCount } }}
        <igntr-icon name="arrowR" [size]="14"></igntr-icon>
      </a>

      @if (loading) {
        <div class="text-[14px] text-slate-400 py-6 text-center">
          {{ 'dashLoading' | transloco }}
        </div>
      } @else if (views.length === 0) {
        <div class="text-[14px] text-slate-400 py-6 text-center">
          {{ 'dashToolsEmpty' | transloco }}
        </div>
      } @else {
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
          @for (tool of views; track tool.id) {
            <a
              [routerLink]="['/toolbox/toolbox-info', tool.id]"
              class="group rounded-xl bg-white overflow-hidden text-left hover:shadow-[0_14px_32px_-20px_rgba(15,23,42,.4)] hover:-translate-y-0.5 transition-all flex flex-col"
              style="border: 1px solid rgba(226,232,240,0.8);"
            >
              <div
                class="relative h-[110px] bg-gradient-to-br from-slate-600 to-slate-800 flex items-center justify-center"
              >
                <span
                  class="w-12 h-12 rounded-full bg-white/95 flex items-center justify-center text-slate-900 shadow-md group-hover:scale-110 transition-transform"
                >
                  <igntr-icon name="scroll" [size]="22"></igntr-icon>
                </span>
                @if (tool.isNew) {
                  <span
                    class="absolute top-2.5 left-2.5 inline-flex items-center gap-1 text-[14px] uppercase tracking-[0.1em] font-medium px-1.5 py-0.5 rounded text-white bg-emerald-500"
                  >
                    {{ 'dashToolBadgeNew' | transloco }}
                  </span>
                }
              </div>

              <div class="p-[14px] flex-1 flex flex-col">
                @if (tool.meta) {
                  <div
                    class="text-[14px] uppercase tracking-[0.12em] text-slate-400 font-medium truncate"
                  >
                    {{ tool.meta }}
                  </div>
                }
                <div
                  class="mt-1 text-[14px] font-semibold text-slate-900 leading-snug line-clamp-2"
                >
                  {{ tool.title }}
                </div>
              </div>
            </a>
          }
        </div>
      }
    </app-dashboard-card>
  `,
})
export class DashboardFeaturedToolsComponent {
  @Input() set tools(value: ToolboxForListDto[]) {
    this._tools = value ?? [];
  }
  get tools(): ToolboxForListDto[] {
    return this._tools;
  }
  @Input() loading = false;

  private _tools: ToolboxForListDto[] = [];

  get totalCount(): number {
    return this._tools.length;
  }

  get views(): ToolView[] {
    const cutoff = Date.now() - NEW_BADGE_DAYS * 24 * 60 * 60 * 1000;
    return [...this._tools]
      .sort((a, b) => this.timeOf(b.createdAt) - this.timeOf(a.createdAt))
      .slice(0, 3)
      .map((t) => ({
        id: t.id,
        title: t.title,
        meta: t.folderNames?.[0] ?? '',
        isNew: this.timeOf(t.createdAt) >= cutoff,
      }));
  }

  private timeOf(value: Date | string | undefined): number {
    if (!value) return 0;
    const ts = new Date(value).getTime();
    return Number.isNaN(ts) ? 0 : ts;
  }
}
